import { useNavigate } from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import { Authenticated, AuthLoading, Unauthenticated } from "convex/react";
import { useMutation, useQuery } from "convex/react";
import { useAuthActions } from "@convex-dev/auth/react";
import { api } from "../../convex/_generated/api";
import { Button } from "@/components/ui/button";

function FullPageNote({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-svh items-center justify-center p-6">
      <div className="w-full max-w-sm text-center">{children}</div>
    </div>
  );
}

/** Runs the first-user bootstrap, then renders children only for admins. */
function AdminGate({ children }: { children: React.ReactNode }) {
  const me = useQuery(api.users.currentUser);
  const ensureSelfAdmin = useMutation(api.users.ensureSelfAdmin);
  const attempted = useRef(false);

  useEffect(() => {
    // Signed in but the user row isn't visible yet, or no admin exists:
    // exactly one bootstrap attempt (first user becomes admin, else no-op).
    if (me !== undefined && !attempted.current) {
      attempted.current = true;
      ensureSelfAdmin({}).catch(() => {
        // Non-admins land here once an admin exists — expected, ignore.
      });
    }
  }, [me, ensureSelfAdmin]);

  if (me === undefined) {
    return (
      <FullPageNote>
        <p className="text-muted-foreground text-sm">Loading your account…</p>
      </FullPageNote>
    );
  }

  if (me === null || me.isAdmin !== true) {
    return <AccessDenied email={me?.email} />;
  }

  return <>{children}</>;
}

function AccessDenied({ email }: { email?: string }) {
  const { signOut } = useAuthActions();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    await signOut();
    navigate({ to: "/login", replace: true });
  };

  return (
    <FullPageNote>
      <div className="flex flex-col gap-3">
        <h1 className="text-lg font-semibold tracking-tight">
          Access pending approval
        </h1>
        <p className="text-muted-foreground text-sm leading-6">
          {email ? (
            <>
              <span className="text-foreground font-medium">{email}</span> is
              signed in, but this account isn&apos;t enabled for Veez HQ yet.
            </>
          ) : (
            <>This account isn&apos;t enabled for Veez HQ yet.</>
          )}{" "}
          Ask an admin to flip <code>isAdmin</code> on your row in Convex
          Dashboard → Data → users, then reload.
        </p>
        <div className="mt-2 flex items-center justify-center gap-2">
          <Button variant="outline" onClick={() => window.location.reload()}>
            Retry
          </Button>
          <Button onClick={handleSignOut}>Sign out</Button>
        </div>
      </div>
    </FullPageNote>
  );
}

function RedirectToLogin() {
  const navigate = useNavigate();
  useEffect(() => {
    navigate({ to: "/login", replace: true });
  }, [navigate]);
  return (
    <FullPageNote>
      <p className="text-muted-foreground text-sm">Redirecting to login…</p>
    </FullPageNote>
  );
}

/**
 * Wrap every protected shell with this. Unauthenticated users go to
 * /login; signed-in non-admins see AccessDenied; admins see the app.
 */
export function AuthGuard({ children }: { children: React.ReactNode }) {
  return (
    <>
      <AuthLoading>
        <FullPageNote>
          <p className="text-muted-foreground text-sm">Loading…</p>
        </FullPageNote>
      </AuthLoading>
      <Unauthenticated>
        <RedirectToLogin />
      </Unauthenticated>
      <Authenticated>
        <AdminGate>{children}</AdminGate>
      </Authenticated>
    </>
  );
}
